import { useEffect, useMemo, useState } from "react";

import PartCategorySubPartsEditor from "../../components/partCategorySubParts/PartCategorySubPartsEditor";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useCategoriesPartial from "../../data/useCategoriesPartial";
import usePartCategorySubParts from "../../data/usePartCategorySubParts";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { IPartCategory } from "../../models/Part";

export default function PartCategorySubParts() {
  const { setInfo } = usePageNameContext();
  const { categories: allCategories } = useCategoriesPartial();
  const { subPartLinks } = usePartCategorySubParts();

  const [selectedCategory, setSelectedCategory] = useState<IPartCategory | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [onlyMains, setOnlyMains] = useState(true);

  useEffect(() => {
    setInfo({
      name: "Category Sub-Parts",
      desc: "Mark which categories are included within a main category",
    });
  }, []);

  /** Main category id -> number of sub-parts attached. */
  const subPartCountByMainId = useMemo(() => {
    const counts = new Map<string, number>();
    (subPartLinks.data?.data ?? []).forEach((link) => {
      counts.set(link.mainCategoryId, (counts.get(link.mainCategoryId) ?? 0) + 1);
    });
    return counts;
  }, [subPartLinks.data?.data]);

  const subPartOfCountById = useMemo(() => {
    const counts = new Map<string, number>();
    (subPartLinks.data?.data ?? []).forEach((link) => {
      counts.set(link.subPartCategoryId, (counts.get(link.subPartCategoryId) ?? 0) + 1);
    });
    return counts;
  }, [subPartLinks.data?.data]);

  const rows = useMemo(() => {
    let list = allCategories.data?.data ?? [];

    if (onlyMains) {
      // hasSubParts comes from the partial projection; fall back to the links list so the
      // filter still works if the partial payload is stale.
      list = list.filter((c) => c.hasSubParts || subPartCountByMainId.has(c.id));
    }

    if (searchTerm) {
      list = list.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [allCategories.data?.data, onlyMains, searchTerm, subPartCountByMainId]);

  const handleSelect = (category: IPartCategory) => {
    setSelectedCategory(category);
    setOpen(true);
  };

  return (
    <>
      <PartCategorySubPartsEditor category={selectedCategory} open={open} setOpen={setOpen} />

      <div className="flex flex-col gap-y-4">
        <div className="flex items-center flex-row gap-x-4">
          <input
            type="text"
            placeholder="Search Categories"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-2"
          />
          <label>Only main categories</label>
          <input
            type="checkbox"
            checked={onlyMains}
            onChange={(e) => setOnlyMains(e.target.checked)}
            className="p-2"
          />
          <span className="ml-auto text-sm text-gray-500">
            Untick to pick any category and give it sub-parts
          </span>
        </div>

        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            {allCategories.isLoading || subPartLinks.isLoading ? <LoadingWheel /> : (
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
                  <thead className="bg-primary-50 text-gray-800">
                    <tr>
                      <th scope="col" className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left">
                        Category Name
                      </th>
                      <th scope="col" className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left">
                        Sub-Parts
                      </th>
                      <th scope="col" className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left">
                        Also A Sub-Part Of
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rows.map((category) => {
                      const subPartCount = subPartCountByMainId.get(category.id) ?? 0;
                      const subPartOfCount = subPartOfCountById.get(category.id) ?? 0;

                      return (
                        <tr
                          key={category.id}
                          onClick={() => handleSelect(category)}
                          className={`hover:bg-gray-100 hover:cursor-pointer ${selectedCategory?.id === category.id ? 'bg-green-100' : ''}`}
                        >
                          <td className="px-3 py-3 border text-sm">
                            {category.name}
                          </td>
                          <td className="px-3 py-3 border text-sm">
                            {subPartCount > 0 ? subPartCount : "-"}
                          </td>
                          <td className="px-3 py-3 border text-sm">
                            {subPartOfCount > 0 ? (
                              <span className="rounded-full border border-blue-300 bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight text-blue-800">
                                {subPartOfCount} {subPartOfCount === 1 ? "category" : "categories"}
                              </span>
                            ) : "-"}
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-3 py-6 border text-sm text-center text-gray-500">
                          {onlyMains
                            ? "No categories have sub-parts yet. Untick \"Only main categories\" to add some."
                            : "No categories match that search."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
